import Button from '@components/Button/Button';
import { ToastType } from '@components/Toast/Toast';
import {
  createStopFacilityDetails,
  facilityDetailsValidationMessage,
  linkStopToSavedAddress,
  schedulingEmailCcError,
  type FacilityLinkStatus,
} from '@common/facilityDetails';
import { InitialStopFormValues } from '@common/stops';
import {
  createFacilityAddress,
  FacilityAddressWriteInput,
  updateLinkedFacilityAddress,
} from '@store/customers/customerDetails/addressBookTab/saveFacilityAddress';
import { AddressBookModel } from '@store/customers/customerDetails/addressBookTab/types';
import { orderStore } from '@store/orders/store';
import { handleToast } from '@utils/utils';
import { Accessor, createEffect, createSignal, Show, untrack } from 'solid-js';

type StopAddressBookSyncProps = {
  data: Accessor<InitialStopFormValues>;
  setField: (field: string, value: unknown) => void;
  customerId: Accessor<number | null | undefined>;
  stopCanSave: () => Promise<boolean>;
};

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const fingerprint = (data: InitialStopFormValues): string => {
  const facility = data.facilityDetails;
  return JSON.stringify({
    locationName: data.locationName,
    address1: data.address1,
    address2: data.address2,
    city: data.city,
    state: data.state,
    zip: data.zip,
    countryCode: data.countryCode,
    contact: data.contact,
    phone: data.phone,
    email: data.email,
    instructions: data.instructions,
    directions: data.directions,
    description: data.description,
    appointmentType: data.facilityAppointmentType ?? '',
    schedulingMethod: data.facilitySchedulingMethod ?? '',
    schedulingMethodDetails: facility?.schedulingMethodDetails ?? '',
    schedulingEmailCc: facility?.schedulingEmailCc ?? '',
    portalUrl: facility?.portalUrl ?? '',
    portalCredentials: facility?.portalCredentials ?? '',
    preferredAppointmentTime: facility?.preferredAppointmentTime ?? '',
    pickupHoursNotes: facility?.pickupHoursNotes ?? '',
    receivingHoursNotes: facility?.receivingHoursNotes ?? '',
    receivingUsesPickupHours: Boolean(facility?.receivingUsesPickupHours),
    pickupHours: facility?.pickupHours ?? [],
    receivingHours: facility?.receivingHours ?? [],
  });
};

type LinkBaseline = {
  isOverridden: boolean;
  linkStatus: FacilityLinkStatus;
};

type StoredBaseline = {
  addressId: string;
  fingerprint: string;
  link: LinkBaseline;
};

const baselinesByStop = new Map<string, StoredBaseline>();

type StopSnapshot = {
  id: number;
  stopOrder: number | null;
};

const loadSnapshots = new Map<number, StopSnapshot[]>();
let snapshotOrderId: number | undefined;

const baselineKey = (orderId: number, loadIndex: number, stopId: number) =>
  `${orderId}:${loadIndex}:${stopId}`;

const sameDraft = (previousOrderId: number, nextOrderId: number): boolean => {
  if (previousOrderId === nextOrderId) return true;
  return previousOrderId <= 0;
};

const unseenStop = (previous: StopSnapshot[], stop: StopSnapshot): boolean =>
  previous.every((old) => old.id !== stop.id);

const replacementStop = (
  previous: StopSnapshot[],
  next: StopSnapshot[],
  prev: StopSnapshot,
  index: number,
): StopSnapshot | undefined => {
  if (prev.id >= 0) return undefined;
  const byOrder =
    prev.stopOrder == null
      ? undefined
      : next.find(
          (stop) =>
            stop.stopOrder === prev.stopOrder && unseenStop(previous, stop),
        );
  if (byOrder != null && byOrder.id > 0) return byOrder;
  if (previous.length !== next.length) return undefined;
  const byPosition = next[index];
  if (byPosition.id <= 0) return undefined;
  if (!unseenStop(previous, byPosition)) return undefined;
  return byPosition;
};

const carryBaselines = (
  previousOrderId: number,
  nextOrderId: number,
  loadIndex: number,
  previous: StopSnapshot[],
  next: StopSnapshot[],
) => {
  previous.forEach((prev, index) => {
    if (next.some((stop) => stop.id === prev.id)) return;
    const baseline = baselinesByStop.get(
      baselineKey(previousOrderId, loadIndex, prev.id),
    );
    if (baseline == null) return;
    const replacement = replacementStop(previous, next, prev, index);
    if (replacement == null) return;
    const nextKey = baselineKey(nextOrderId, loadIndex, replacement.id);
    if (!baselinesByStop.has(nextKey)) {
      baselinesByStop.set(nextKey, baseline);
    }
    baselinesByStop.delete(baselineKey(previousOrderId, loadIndex, prev.id));
  });
};

const syncLoadBaselines = () => {
  const nextOrderId = orderStore.order.id;
  orderStore.order.loads.forEach((load, loadIndex) => {
    const next = (load.stops ?? []).map((stop) => ({
      id: stop.id,
      stopOrder: stop.stopOrder ?? null,
    }));
    const previous = loadSnapshots.get(loadIndex);
    if (
      previous != null &&
      snapshotOrderId != null &&
      sameDraft(snapshotOrderId, nextOrderId)
    ) {
      carryBaselines(snapshotOrderId, nextOrderId, loadIndex, previous, next);
    }
    loadSnapshots.set(loadIndex, next);
  });
  snapshotOrderId = nextOrderId;
};

export const StopAddressBookSync = (props: StopAddressBookSyncProps) => {
  const [snapshot, setSnapshot] = createSignal<string | null>(null);
  const [baselineLink, setBaselineLink] = createSignal<LinkBaseline | null>(
    null,
  );
  const [saving, setSaving] = createSignal(false);
  let trackedOrderId: number | undefined;
  let trackedLoadIndex: number | undefined;
  let trackedStopId: number | undefined;
  let trackedAddressId: string | null | undefined;

  const rememberBaseline = (data: InitialStopFormValues) => {
    const details = data.facilityDetails;
    const nextFingerprint = fingerprint(data);
    const link =
      details == null
        ? null
        : {
            isOverridden: details.isOverridden,
            linkStatus: details.linkStatus,
          };
    setSnapshot(nextFingerprint);
    setBaselineLink(link);
    const addressId = details?.customerAddressId ?? null;
    if (addressId == null || link == null) return;
    baselinesByStop.set(
      baselineKey(orderStore.order.id, orderStore.activeTab.index, data.id),
      {
        addressId,
        fingerprint: nextFingerprint,
        link,
      },
    );
  };

  const linkedId = () =>
    props.data().facilityDetails?.customerAddressId ?? null;

  const dirty = () => {
    const base = snapshot();
    if (base == null || linkedId() == null) return false;
    return fingerprint(props.data()) !== base;
  };

  createEffect(syncLoadBaselines);

  createEffect(() => {
    const orderId = orderStore.order.id;
    const loadIndex = orderStore.activeTab.index;
    const stopId = props.data().id;
    const addressId = linkedId();
    if (
      orderId === trackedOrderId &&
      loadIndex === trackedLoadIndex &&
      stopId === trackedStopId &&
      addressId === trackedAddressId
    ) {
      return;
    }
    trackedOrderId = orderId;
    trackedLoadIndex = loadIndex;
    trackedStopId = stopId;
    trackedAddressId = addressId;
    if (addressId == null) {
      untrack(() => {
        setSnapshot(null);
        setBaselineLink(null);
      });
      return;
    }
    const stored = baselinesByStop.get(baselineKey(orderId, loadIndex, stopId));
    if (stored != null && stored.addressId === addressId) {
      untrack(() => {
        setSnapshot(stored.fingerprint);
        setBaselineLink(stored.link);
      });
      return;
    }
    untrack(() => {
      rememberBaseline(props.data());
    });
  });

  createEffect(() => {
    if (saving()) return;
    const current = props.data().facilityDetails;
    if (current == null || current.customerAddressId == null) return;
    const base = baselineLink();
    if (base == null) return;
    const matchesBaseline =
      current.isOverridden === base.isOverridden &&
      current.linkStatus === base.linkStatus;
    if (!dirty()) {
      if (matchesBaseline) return;
      props.setField('facilityDetails', {
        ...current,
        isOverridden: base.isOverridden,
        linkStatus: base.linkStatus,
      });
      return;
    }
    if (current.isOverridden) return;
    props.setField('facilityDetails', {
      ...current,
      isOverridden: true,
      linkStatus: 'Overridden',
    });
  });

  const stopInput = (): FacilityAddressWriteInput => {
    const data = props.data();
    return {
      customerId: props.customerId() ?? 0,
      customerAddressId: data.facilityDetails?.customerAddressId ?? null,
      locationName: data.locationName,
      address1: data.address1,
      address2: data.address2,
      city: data.city,
      state: data.state,
      zip: data.zip,
      countryCode: data.countryCode,
      contact: data.contact,
      phone: data.phone,
      email: data.email,
      instructions: data.instructions,
      directions: data.directions,
      description: data.description,
      facilityDetails: data.facilityDetails ?? createStopFacilityDetails(),
    };
  };

  const addressReady = (): boolean => {
    if (!props.customerId()) {
      handleToast(
        ToastType.Caution,
        'Please select a customer for the order to add to address book.',
      );
      return false;
    }
    const data = props.data();
    if (
      !data.locationName.trim() ||
      !data.address1.trim() ||
      !data.city.trim() ||
      !data.state.trim() ||
      !data.zip.trim()
    ) {
      handleToast(
        ToastType.Caution,
        'Complete all required fields before saving this address.',
      );
      return false;
    }
    if (data.email && !EMAIL_PATTERN.test(data.email)) {
      handleToast(ToastType.Caution, 'Please enter a valid email address');
      return false;
    }
    const ccError = schedulingEmailCcError(
      data.facilityDetails?.schedulingEmailCc,
      true,
    );
    if (ccError) {
      handleToast(ToastType.Caution, ccError);
      return false;
    }
    return true;
  };

  const draftKey = () => `${linkedId() ?? ''}:${fingerprint(props.data())}`;

  const applySaved = (saved: AddressBookModel) => {
    const current = props.data().facilityDetails ?? createStopFacilityDetails();
    const linked = linkStopToSavedAddress(current, saved);
    trackedOrderId = orderStore.order.id;
    trackedLoadIndex = orderStore.activeTab.index;
    trackedStopId = props.data().id;
    trackedAddressId = linked.customerAddressId ?? null;
    props.setField('facilityAppointmentType', linked.appointmentType);
    props.setField('facilitySchedulingMethod', linked.schedulingMethod);
    props.setField('facilityDetails', linked);
    rememberBaseline({
      ...props.data(),
      facilityDetails: linked,
      facilityAppointmentType: linked.appointmentType,
      facilitySchedulingMethod: linked.schedulingMethod,
    });
  };

  const reportSaveError = (saveError: unknown) => {
    const validation = facilityDetailsValidationMessage(saveError);
    if (validation) {
      handleToast(ToastType.Error, validation);
      return;
    }
    const message =
      saveError instanceof Error
        ? saveError.message
        : 'Failed to save address book entry';
    handleToast(ToastType.Error, message);
  };

  const saveNewAddress = async () => {
    if (saving() || !addressReady()) return;
    setSaving(true);
    try {
      const ready = await props.stopCanSave();
      if (!ready) return;
      const submitted = draftKey();
      const address = await createFacilityAddress({
        ...stopInput(),
        customerAddressId: null,
      });
      if (draftKey() !== submitted) {
        handleToast(
          ToastType.Caution,
          'This stop changed while the address was saving, so the address book result was not applied.',
        );
        return;
      }
      applySaved(address);
      handleToast(ToastType.Success, 'Address added to Address Book.');
    } catch (saveError) {
      reportSaveError(saveError);
    } finally {
      setSaving(false);
    }
  };

  const updateAddress = async () => {
    if (saving() || !addressReady()) return;
    setSaving(true);
    try {
      const submitted = draftKey();
      const saved = await updateLinkedFacilityAddress(stopInput());
      if (draftKey() !== submitted) {
        handleToast(
          ToastType.Caution,
          'This stop changed while the address was saving, so the address book result was not applied.',
        );
        return;
      }
      applySaved(saved);
      handleToast(ToastType.Success, 'Address Book entry updated.');
    } catch (saveError) {
      reportSaveError(saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Show when={dirty()}>
      <div
        class="mb-3 flex flex-col gap-3 rounded border border-caution-yellow bg-caution-yellow px-3 py-3"
        data-testid="stop-address-book-dirty"
      >
        <span class="text-sm font-semibold text-announcement-warning">
          Address Book fields have changed. Update existing address book entry
          or save as new address.
        </span>
        <div class="flex flex-wrap gap-2">
          <Button
            variant="outlined"
            size="sm"
            type="button"
            label="UPDATE ADDRESS BOOK ENTRY"
            isLoading={saving()}
            disabled={saving()}
            onClick={() => {
              void updateAddress();
            }}
            dataTestId="stop-update-address-book"
          />
          <Button
            variant="contained"
            color="primary"
            size="sm"
            type="button"
            label="SAVE AS NEW ADDRESS"
            isLoading={saving()}
            disabled={saving()}
            onClick={() => {
              void saveNewAddress();
            }}
            dataTestId="stop-save-as-new-address"
          />
        </div>
      </div>
    </Show>
  );
};
